import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import Analytics from 'arbitrip-common/client/analytics';
import { isRetroactivePointsUsageEnabled } from 'arbitrip-common/client/utils/RetroactivePoints';

import BookingsConstants from '@/constants/BookingsConstants';
import { BANK_TRANSFER_UI_ENABLED } from '@/constants/BankTransferConstants';
import BookingsActions from '@/actions/BookingsActions';
import BookingsStore from '@/stores/BookingsStore';
import ProfileStore from '@/stores/ProfileStore';
import PointsStore from '@/stores/PointsStore';

import NewBookingsFiltersWrapComponent from './NewBookingsFiltersWrapComponent';
import PrivateTravelBookingsHeader from './PrivateTravelBookingsHeader';
import BookingsTable from './BookingsTable.react';
import BookingDrawer from './BookingDrawer.react';
import BookingsTabs from './BookingsTabs';
import ExportButton from './ExportButton';
import SelectToPayToggle from './SelectToPayToggle';
import SelectionFooterBar from './SelectionFooterBar.react';
import SettlePaymentMain from './SettlePayment/SettlePaymentMain.react';
import { getSelectableBookings } from './utils/eligibility';
import {
    ACCOUNT_PAYMENT_SCOPE,
    isAccountBankTransferMode,
    isAccountBookingsScope,
} from '@/utils/AccountBankTransfer';
import Snackbar from '@mui/material/Snackbar';
import InfoIcon from '@mui/icons-material/Info';
import ReservationConstants from '@/constants/ReservationConstants';
import { EXTRAS, TABS_CONFIG, TAB_LABELS } from './consts';
import { toggleZendeskMessenger } from 'arbitrip-common/client/analytics/providers/zendesk/utils';

import TitleManager from '@/utils/TitleManager';
import DocumentTitle from '@/components/general/DocumentTitle.react';
import queryString from 'query-string';
import useSamlPageVerification, { isSamlRedirectNeeded } from '@/hooks/useSamlPageVerification';
import Ajax from '@/utils/Ajax';

const { ORDER_BY, ORDER } = BookingsConstants;

const ESCAPE_KEY_CODE = 27;

const PAYMENT_SCOPE_FILTER_KEY = 'payment_scope';
const UNPAID_TAB_FILTER_KEY = TABS_CONFIG.find((tab) => tab.label === TAB_LABELS.UNPAID).filterKey;

const snackbar_messages = {
    [ReservationConstants.STATUS.INITIAL]: '',
    [ReservationConstants.STATUS.BUSY]: 'Sending booking confirmation...',
    [ReservationConstants.STATUS.SUCCESS]: 'Booking confirmation sent',
    [ReservationConstants.STATUS.FAILED]: 'Booking confirmation failed',
};

const initFilters = {
    search: '',
    where: '',
    destination: '',
    start_date: null,
    end_date: null,
    agent_name: '',
    agent_id: null,
    contract_id: null,
    company_name: '',
    traveler_first_name: '',
    traveler_last_name: '',
    reservation_id: '',
    hotel_name: '',
    hotel_id: null,
    show_confirmed: false,
    travel_booster_not_synced: false,
    show_upcoming: false,
    cancellation_deadline: false,
    show_ongoing: false,
    furthest_deadline: false,
    closest_deadline_excludes_passed_due: false,
    closest_deadline_includes_passed_due: false,
    extra: null,
    bookings_from_branch: '',
    agency_branches: [],
    cancellation_deadline_start: null,
    cancellation_deadline_end: null,
};

const initPagination = {
    page: 0,
    rows_per_page: BookingsConstants.BOOKINGS_LIMIT,
};

const initSort = {
    order_by: '',
    order: '',
};

function getState() {
    const profile = ProfileStore.getProfile();

    return {
        profile,

        bookings: BookingsStore.getItems(),
        bookings_count: BookingsStore.getCount(),

        status: BookingsStore.getStatus(),
        bookingsCountStatus: BookingsStore.getCountStatus(),
        download_status: BookingsStore.getDownloadStatus(),

        sync_combtas_status: BookingsStore.getSyncCombtasStatus(),
        sync_travel_booster_status: BookingsStore.getSyncTravelBoosterStatus(),
        booking_confirmation_status: BookingsStore.getSendBookingConfirmationStatus(),

        points: PointsStore.getPoints(),
        update_points_usage_status: PointsStore.getUpdatePointsUsageStatus(),

        bookings_page_title: TitleManager.buildDefaultPageTitle({ profile, page: 'Bookings' }),

        settle_payment_status: null, // TODO:
    };
}

const createTabIndexMap = (tabsConfig) => {
    return tabsConfig.reduce((map, tab, index) => {
        map[tab.label] = index;
        return map;
    }, {});
};

const BookingsPage = () => {
    const { t, i18n } = useTranslation();

    const [state, setState] = useState(getState());

    const [search, setSearch] = useState(initFilters.search);
    const [filters, setFilters] = useState(initFilters);
    const [pagination, setPagination] = useState(initPagination);
    const [sort, setSort] = useState(initSort);

    const [openDrawer, setOpenDrawer] = useState(false);
    const [bookingDrawer, setBookingDrawer] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const navigate = useNavigate();
    const { booking_id: bookingIdParam } = useParams();
    const location = useLocation();
    const bookingIdFromLocation = location?.state?.id;

    useSamlPageVerification();

    const [initialLoad, setInitialLoad] = useState(false);
    const prevStatus = useRef(state.status);

    const [selectedTab, setSelectedTab] = useState(0);

    const [isSelectMode, setIsSelectMode] = useState(false);
    const [selectedBookingsMap, setSelectedBookingsMap] = useState({});

    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [bookingsForPayment, setBookingsForPayment] = useState([]);

    const displayFilters = useMemo(() => {
        const { agent, super_agent, freelance_agent, travel_manager, private_travel_only } = state.profile;

        return agent || super_agent || freelance_agent || travel_manager || !private_travel_only;
    }, [state.profile]);

    const accountMode = isAccountBankTransferMode(state.profile);
    const accountScope = isAccountBookingsScope(state.profile);
    const payLaterAllowed = !!state.profile.pay_later_allowed;
    const canSettlePayments = BANK_TRANSFER_UI_ENABLED && (payLaterAllowed || accountMode);

    const tabsConfig = useMemo(() => {
        return TABS_CONFIG.map((tab) => {
            const translatedLabel = t(tab.label);
            if (tab.label === TAB_LABELS.UNPAID || tab.label === TAB_LABELS.PAID) {
                return {
                    ...tab,
                    label: translatedLabel,
                    shouldDisplay: payLaterAllowed || accountMode,
                };
            }
            if (tab.label === TAB_LABELS.MISSING_DOCKET) {
                const showTravelBooster =
                    state.profile.agent && state.profile.settings?.travel_booster_integration_enabled;
                return {
                    ...tab,
                    label: translatedLabel,
                    shouldDisplay: showTravelBooster,
                };
            }
            return { ...tab, label: translatedLabel };
        }).filter((tab) => tab.shouldDisplay);
    }, [i18n.language]);

    const tabIndexMap = useMemo(() => createTabIndexMap(tabsConfig), [tabsConfig]);

    useEffect(() => {
        if (
            prevStatus.current === BookingsConstants.STATUS.BUSY &&
            state.status === BookingsConstants.STATUS.SUCCESS &&
            !initialLoad
        ) {
            if (selectedTab === tabIndexMap[TAB_LABELS.UPCOMING] && state.bookings.length === 0) {
                setSelectedTab(tabIndexMap[TAB_LABELS.ALL]);
                setTimeout(() => {
                    setInitialLoad(true);
                }, 1000);
            } else {
                setInitialLoad(true);
            }
        }
        prevStatus.current = state.status;
    }, [state.status]);

    useEffect(() => {
        const params = queryString.parse(window.location.search);
        if (
            state.status === BookingsConstants.STATUS.INITIAL &&
            !initialLoad &&
            params.unpaid &&
            tabIndexMap[TAB_LABELS.UNPAID] &&
            selectedTab !== tabIndexMap[TAB_LABELS.UNPAID]
        ) {
            setSelectedTab(tabIndexMap[TAB_LABELS.UNPAID]);
        }
    }, [selectedTab]);

    useEffect(() => {
        BookingsStore.addChangeListener(_onChange);
        PointsStore.addChangeListener(_onChange);

        getBookings();

        Analytics.actions.views.bookingsPage(state.profile);

        return () => {
            BookingsStore.removeChangeListener(_onChange);
            PointsStore.removeChangeListener(_onChange);
        };
    }, []);

    const getSortConfigMap = (tabsConfig, tabIndexMap) => ({
        [tabsConfig[tabIndexMap[TAB_LABELS.UPCOMING]]?.label]: { order_by: ORDER_BY.CHECK_IN, order: ORDER.ASC },
        [tabsConfig[tabIndexMap[TAB_LABELS.ONGOING]]?.label]: { order_by: ORDER_BY.CHECK_IN, order: ORDER.ASC },
        [tabsConfig[tabIndexMap[TAB_LABELS.ENDED]]?.label]: { order_by: ORDER_BY.CHECK_OUT, order: ORDER.DESC },
        [tabsConfig[tabIndexMap[TAB_LABELS.UNPAID]]?.label]: { order_by: ORDER_BY.CHECK_IN, order: ORDER.ASC },
    });

    useEffect(() => {
        if (displayFilters) {
            const tabLabel = tabsConfig[selectedTab]?.label;
            const sortConfigMap = getSortConfigMap(tabsConfig, tabIndexMap);
            const sortConfig = getSortForTab(tabLabel, sortConfigMap);
            setSort(sortConfig);

            // get bookings on tab change if pagination the same as initPagination
            if (pagination.page === 0) {
                return getBookings();
            }

            setPagination(initPagination);
        }
    }, [selectedTab, tabsConfig, tabIndexMap]);

    useEffect(() => {
        setPagination(initPagination);
        getBookings();
    }, [filters]);

    useEffect(() => {
        // don't get count on pagination change unless agent + count on every 100 records
        const shouldGetCount = pagination.page === 0 || !(((pagination.page + 1) * pagination.rows_per_page) % 100);
        getBookings(shouldGetCount);
    }, [pagination, sort]);

    useEffect(() => {
        const bookingId = bookingIdParam || bookingIdFromLocation;

        if (!bookingId) return;

        const localBooking = state.bookings.find(
            (b) => b.id?.toString() === bookingId.toString() || b.short_id?.toString() === bookingId.toString(),
        );

        if (localBooking) {
            setOpenDrawer(true);
            setBookingDrawer(localBooking);
            navigate('/bookings', { replace: true, state: {} });
        } else {
            let getBookingsAjaxCallback = Ajax.getPersonalBookings;
            const fetchDrawerBooking = (drawerFilters, onNotFound) =>
                getBookingsAjaxCallback(0, 1, drawerFilters)
                    .done((res) => {
                        const paramBooking = res?.bookings?.[0];
                        if (paramBooking) {
                            const sanitizedBooking = {
                                ...paramBooking,
                                id: paramBooking.id || paramBooking._id,
                                travelers: paramBooking.travelers || paramBooking.travellers || [],
                            };
                            setOpenDrawer(true);
                            setBookingDrawer(sanitizedBooking);
                        } else if (onNotFound) {
                            onNotFound();
                        }
                    })
                    .fail((err) => {
                        console.error('Error silently fetching specific booking for drawer: ', err);
                        if (onNotFound) {
                            onNotFound();
                        }
                    });

            const plainFilters = { reservation_id: bookingId };
            // Account debt carries its credit-line/request state only from the Account scope, which the
            // server honours together with the Unpaid tab filter; anything else falls back to the plain lookup.
            if (isAccountBookingsScope(state.profile)) {
                fetchDrawerBooking(
                    {
                        ...plainFilters,
                        [UNPAID_TAB_FILTER_KEY]: UNPAID_TAB_FILTER_KEY,
                        [PAYMENT_SCOPE_FILTER_KEY]: ACCOUNT_PAYMENT_SCOPE,
                    },
                    () => fetchDrawerBooking(plainFilters),
                );
            } else {
                fetchDrawerBooking(plainFilters);
            }
        }
    }, [bookingIdParam, bookingIdFromLocation, state.bookings, state.profile, navigate]);

    const getSortForTab = (tabLabel) => {
        const sortMap = {
            [TAB_LABELS.UPCOMING]: { order_by: ORDER_BY.CHECK_IN, order: ORDER.ASC },
            [TAB_LABELS.ONGOING]: { order_by: ORDER_BY.CHECK_IN, order: ORDER.ASC },
            [TAB_LABELS.ARCHIVE]: { order_by: ORDER_BY.CHECK_OUT, order: ORDER.DESC },
            [TAB_LABELS.UNPAID]: { order_by: ORDER_BY.CANCELLATION_DEADLINE, order: ORDER.ASC },
        };

        return sortMap[tabLabel] || initSort;
    };

    const getActiveFilters = () => {
        const tabFilter = tabsConfig[selectedTab]?.filterKey;
        return {
            ...(!_.isEmpty(filters.search) && { search: filters.search }),
            ...(!_.isEmpty(filters.where) && { destination_hotel_name: filters.where }),
            ...(!_.isEmpty(filters.destination) && { destination: filters.destination }),
            ...(moment.isMoment(filters.start_date) && { after_date: moment(filters.start_date).format('YYYY-MM-DD') }),
            ...(moment.isMoment(filters.end_date) && { before_date: moment(filters.end_date).format('YYYY-MM-DD') }),
            ...(!_.isEmpty(filters.agent_id) && { agent_id: filters.agent_id }),
            ...(!_.isEmpty(filters.contract_id) && { contract_id: filters.contract_id }),
            ...(!_.isEmpty(filters.company_name) && { company_name: filters.company_name }),
            ...(!_.isEmpty(filters.traveler_first_name) && { traveler_first_name: filters.traveler_first_name }),
            ...(!_.isEmpty(filters.traveler_last_name) && { traveler_last_name: filters.traveler_last_name }),
            ...(!_.isEmpty(filters.reservation_id) && { reservation_id: filters.reservation_id }),
            ...(!_.isEmpty(filters.hotel_id) && { hotel_id: filters.hotel_id }),
            ...(!_.isEmpty(filters.hotel_name) && { hotel_name: filters.hotel_name }),
            ...(filters.extra === EXTRAS.SHOW_CONFIRMED && { [EXTRAS.SHOW_CONFIRMED]: EXTRAS.SHOW_CONFIRMED }),
            ...(filters.travel_booster_not_synced && { travel_booster_not_synced: filters.travel_booster_not_synced }),
            ...(filters.extra === EXTRAS.SHOW_UPCOMING && { [EXTRAS.SHOW_UPCOMING]: EXTRAS.SHOW_UPCOMING }),
            ...(filters.extra === EXTRAS.CANCELLATION_DEADLINE && {
                [EXTRAS.CANCELLATION_DEADLINE]: EXTRAS.CANCELLATION_DEADLINE,
            }),
            ...(filters.extra === EXTRAS.SHOW_ONGOING && { [EXTRAS.SHOW_ONGOING]: EXTRAS.SHOW_ONGOING }),
            ...(filters.bookings_from_branch && { [EXTRAS.BOOKINGS_FROM_BRANCH]: filters.bookings_from_branch }),
            ...(filters.agency_branches.length && { [EXTRAS.AGENCY_BRANCHES]: filters.agency_branches.join(',') }),
            ...(filters.extra === EXTRAS.CLOSEST_DEADLINE && { [EXTRAS.CLOSEST_DEADLINE]: EXTRAS.CLOSEST_DEADLINE }),
            ...(filters.extra === EXTRAS.CLOSEST_DEADLINE_PASSED && {
                [EXTRAS.CLOSEST_DEADLINE_PASSED]: EXTRAS.CLOSEST_DEADLINE_PASSED,
            }),
            ...(filters.extra === EXTRAS.FURTHEST_DEADLINE && { [EXTRAS.FURTHEST_DEADLINE]: EXTRAS.FURTHEST_DEADLINE }),
            ...(moment.isMoment(filters.cancellation_deadline_start) && {
                cancellation_deadline_after: moment(filters.cancellation_deadline_start).format('YYYY-MM-DD'),
            }),
            ...(moment.isMoment(filters.cancellation_deadline_end) && {
                cancellation_deadline_before: moment(filters.cancellation_deadline_end).format('YYYY-MM-DD'),
            }),
            ...(displayFilters && tabFilter && { [tabFilter]: tabFilter }),
            ...(displayFilters && isAccountScopeTab() && { [PAYMENT_SCOPE_FILTER_KEY]: ACCOUNT_PAYMENT_SCOPE }),
        };
    };

    // The server honours `payment_scope` only together with the Unpaid / Paid tab filter.
    const isAccountScopeTab = () => {
        const tabLabel = tabsConfig[selectedTab]?.label;
        return accountScope && (tabLabel === t(TAB_LABELS.UNPAID) || tabLabel === t(TAB_LABELS.PAID));
    };

    const getActivePagination = () => ({
        page: pagination.page > 0 ? pagination.page : 0,
        rows_per_page: pagination.rows_per_page > 0 ? pagination.rows_per_page : BookingsConstants.BOOKINGS_LIMIT,
    });

    const getActiveSort = () => ({
        ...(!_.isEmpty(sort.order_by) && { order_by: sort.order_by }),
        ...(!_.isEmpty(sort.order) && { order: sort.order }),
    });

    const getActiveSkipLimit = () => {
        const active_pagination = getActivePagination();

        const limit =
            active_pagination.rows_per_page > 0 ? active_pagination.rows_per_page : BookingsConstants.BOOKINGS_LIMIT;

        return {
            skip: active_pagination.page > 0 ? active_pagination.page * limit : 0,

            limit,
        };
    };

    const sendFiltersAnalytics = () => {
        const filters = getActiveFilters();

        // send all filters to analytics
        for (const [key, value] of Object.entries(filters)) {
            Analytics.actions.interactions.filteredBookings(profile, _.startCase(key), value);
        }
    };

    const getBookings = (shouldGetCount = true) => {
        if (isSamlRedirectNeeded()) return;
        const { skip, limit } = getActiveSkipLimit();
        const { order_by, order } = getActiveSort();

        const filters = getActiveFilters();

        if (shouldGetCount) {
            BookingsActions.getBookingsCountDebounced(skip, limit, filters, order_by, order);
        }
        BookingsActions.getBookingsDebounced(skip, limit, filters, order_by, order);
        sendFiltersAnalytics();
        setBookingDrawer(null);
    };

    const _onChange = () => {
        setState(getState());
    };

    const openBookingDrawer = (booking) => {
        if (booking.id === _.get(bookingDrawer, 'id')) {
            // if row is already open, close it
            bookingDrawerOnClose();
        } else {
            toggleZendeskMessenger('hide');

            setOpenDrawer(true);
            setBookingDrawer(booking);

            const redeemable = isRetroactivePointsUsageEnabled({ profile: state?.profile, booking });
            Analytics.actions.interactions.openedBookingsSidepanel(booking?.id, redeemable);
        }
    };

    const updateBookingDrawer = (booking) => {
        if (booking && booking.id === _.get(bookingDrawer, 'id')) {
            setBookingDrawer(booking);
        }
    };

    const bookingDrawerOnClose = () => {
        setOpenDrawer(false);

        toggleZendeskMessenger('show');
        if (bookingIdParam || bookingIdFromLocation) {
            navigate('/bookings', { replace: true });
        }
        setTimeout(() => {
            setBookingDrawer(false);
        }, 400);
    };

    const onCloseSnackBar = () => {
        setSnackbarOpen(false);
    };

    const handleDeleteTag = (key) => {
        // Set bookings status to transition
        // to avoid a state where there are no filters
        // and the bookings busy state is not called yet
        BookingsActions.setBookingsTransitionStatus();

        if (key === 'search') {
            setSearch(initFilters.search);
        }

        if (key === 'reservation_id' && bookingIdParam) {
            navigate('/bookings');
        }

        // remove tag and set to original data
        setFilters((prev) => {
            const next = {
                ...prev,
                [key]: initFilters[key], // always reset the one being cleared
            };

            if (key === EXTRAS.HOTEL_NAME) {
                // for hotel name also reset hotel id
                next[EXTRAS.HOTEL_ID] = initFilters[EXTRAS.HOTEL_ID];
            }

            if (key === EXTRAS.BOOKINGS_FROM_BRANCH || key === EXTRAS.AGENT_NAME) {
                // for branch also reset agent name and id
                next[EXTRAS.AGENT_ID] = initFilters[EXTRAS.AGENT_ID];
                next[EXTRAS.AGENT_NAME] = initFilters[EXTRAS.AGENT_NAME];
            }

            return next;
        });

        setPagination(initPagination);
    };

    const handleToggleBooking = (bookingId) => {
        setSelectedBookingsMap((prev) => {
            if (prev[bookingId]) {
                const next = { ...prev };
                delete next[bookingId];
                return next;
            }

            const booking = state.bookings.find((b) => b.id === bookingId);
            if (!booking) {
                return prev;
            }

            return { ...prev, [bookingId]: booking };
        });
    };

    const handleToggleSelectAll = () => {
        const selectableBookings = getSelectableBookings(state.profile, state.bookings);

        setSelectedBookingsMap((prev) => {
            const allSelected = selectableBookings.length > 0 && selectableBookings.every((booking) => prev[booking.id]);

            const next = { ...prev };
            selectableBookings.forEach((booking) => {
                if (allSelected) {
                    delete next[booking.id];
                } else {
                    next[booking.id] = booking;
                }
            });

            return next;
        });
    };

    const handlePayNow = () => {
        setBookingsForPayment(selectedBookings);
        setIsPaymentModalOpen(true);
    };

    const handlePaymentModalKeyDown = (e) => {
        if (e.keyCode === ESCAPE_KEY_CODE && isPaymentModalOpen) {
            setIsPaymentModalOpen(false);
        }
    };

    const handleBankTransferSubmitSuccess = () => {
        setSelectedBookingsMap({});
        setBookingsForPayment([]);
        getBookings();
    };

    const handleToggleSelectMode = () => {
        if (isSelectMode) {
            setIsSelectMode(false);
            setSelectedTab(tabIndexMap[t(TAB_LABELS.ALL)]);
            setSelectedBookingsMap({});
        } else {
            setIsSelectMode(true);
            setSelectedTab(tabIndexMap[t(TAB_LABELS.UNPAID)]);
        }
    };

    const handleTabChange = (newValue) => {
        if (isSelectMode && tabsConfig[newValue]?.label !== t(TAB_LABELS.UNPAID)) {
            setIsSelectMode(false);
            setSelectedBookingsMap({});
        }

        setSelectedTab(newValue);
    };

    const {
        profile,
        bookings,
        bookings_count,
        status,
        download_status,
        sync_combtas_status,
        sync_travel_booster_status,
        points,
        update_points_usage_status,
        bookings_page_title,
        settle_payment_status,
    } = state;

    const exportToCsvDisabled = useMemo(() => {
        return !bookings || bookings.length === 0 || status === BookingsConstants.STATUS.BUSY;
    }, [bookings, status]);

    const activeFilters = useMemo(
        () => getActiveFilters(),
        [filters, pagination, sort, selectedTab, tabsConfig, tabIndexMap],
    );

    const activeTab = tabsConfig[selectedTab]?.label;

    const showExport = useMemo(() => {
        const { agent, super_agent, travel_manager } = profile;
        return agent || super_agent || travel_manager;
    }, [profile]);

    const selectedBookings = useMemo(
        () => Object.values(selectedBookingsMap),
        [selectedBookingsMap],
    );

    return (
        <DocumentTitle title={bookings_page_title}>
            <div className="new-bookings-page">
                {displayFilters ? (
                    <>
                        <NewBookingsFiltersWrapComponent
                            status={status}
                            downloadStatus={download_status}
                            profile={profile}
                            bookings={bookings}
                            initFilters={initFilters}
                            filters={filters}
                            setFilters={setFilters}
                            initPagination={initPagination}
                            setPagination={setPagination}
                            initSort={initSort}
                            setSort={setSort}
                            search={search}
                            setSearch={setSearch}
                            activeFilters={activeFilters}
                            handleDeleteTag={handleDeleteTag}
                            activeTab={activeTab}
                        />
                        <div className="tabs-export-container">
                            <div className="tabs-export-container__content">
                                <BookingsTabs
                                    selectedTab={selectedTab}
                                    onChange={(_, newValue) => handleTabChange(newValue)}
                                    showLoader={!initialLoad}
                                    tabsConfig={tabsConfig}
                                />
                                <div className="tabs-export-container__actions">
                                    {canSettlePayments && (
                                        <SelectToPayToggle isSelectMode={isSelectMode} onChange={handleToggleSelectMode} />
                                    )}
                                    {showExport && (
                                        <ExportButton
                                            profile={profile}
                                            disabled={exportToCsvDisabled}
                                            downloadStatus={download_status}
                                            activeFilters={activeFilters}
                                            activeTab={activeTab}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <PrivateTravelBookingsHeader />
                )}
                {displayFilters && accountScope && activeTab === t(TAB_LABELS.UNPAID) && (
                    <div className="account-pre-credit-line-banner" data-testid="account-pre-credit-line-banner">
                        <InfoIcon className="account-pre-credit-line-banner__icon" />
                        <span>{t('Bookings.Account.PreCreditLineBanner')}</span>
                    </div>
                )}
                <BookingsTable
                    showLoader={!initialLoad}
                    displayTabs={displayFilters}
                    profile={profile}
                    bookings={bookings}
                    selectedBooking={bookingDrawer}
                    openDrawer={openBookingDrawer}
                    bookingsCount={bookings_count}
                    status={status}
                    bookingsCountStatus={state.bookingsCountStatus}
                    filters={filters}
                    pagination={pagination}
                    setPagination={setPagination}
                    sort={sort}
                    setSort={setSort}
                    points={points}
                    updateDrawer={updateBookingDrawer}
                    isSelectMode={isSelectMode}
                    selectedBookingIds={selectedBookingsMap}
                    onToggleBooking={handleToggleBooking}
                    onToggleSelectAll={handleToggleSelectAll}
                />

                <BookingDrawer
                    open={openDrawer}
                    onClose={bookingDrawerOnClose}
                    booking={bookingDrawer}
                    profile={profile}
                    syncTravelBoosterStatus={sync_travel_booster_status}
                    sync_combtas_status={sync_combtas_status}
                    confirmationStatus={state.booking_confirmation_status}
                    points={points}
                    updatePointsUsageStatus={update_points_usage_status}
                    settlePaymentStatus={settle_payment_status}
                />

                <Snackbar
                    open={snackbarOpen}
                    message={t(snackbar_messages[state.booking_confirmation_status]) || ''}
                    autoHideDuration={4000}
                    onClose={onCloseSnackBar}
                />

                {selectedBookings.length > 0 && (
                    <SelectionFooterBar selectedBookings={selectedBookings} onPayNow={handlePayNow} />
                )}

                {canSettlePayments && bookingsForPayment.length > 0 && (
                    <SettlePaymentMain
                        bookings={bookingsForPayment}
                        profile={profile}
                        onKeyDown={handlePaymentModalKeyDown}
                        settlePaymentStatus={settle_payment_status}
                        showSettlePaymentModal={isPaymentModalOpen}
                        setShowSettlePaymentModal={setIsPaymentModalOpen}
                        onBankTransferSubmitSuccess={handleBankTransferSubmitSuccess}
                    />
                )}
            </div>
        </DocumentTitle>
    );
};

export default BookingsPage;
